// src/pages/ChatBot/evals/golden-set.tsx
//
// Golden Set sub-panel of the Evaluation tab, split out of EvalsPanel so that
// file stays focused on the run lifecycle + polling. Lists the curated eval
// corpus (the query-logs API with evalSet=true) in a paginated table; admins
// create entries (CustomAnswerModal, created straight into the set), edit their
// custom answers, and remove them from the set (a reversible PATCH evalSet:false,
// no confirm). An entry is "ready" once it has a reference answer — a curated
// custom answer, or the chatbot's own served answer — and runs score those.
// Readiness is reported up via onStats so the parent can gate the Run button.
// Uses the eval-* classes from EvalsPanel.css, which the parent that mounts this
// imports.

import { useCallback, useEffect, useState } from 'react';
import { fetchQueryLogs, patchQueryLogAudit } from '../query-logs/api';
import { formatDateTime } from '../query-logs/derive';
import type { ProjectedQueryLog } from '../query-logs/types';
import CustomAnswerModal from '../query-logs/CustomAnswerModal';

// What GoldenSetSection reports up so EvalsPanel can gate the Run button. Only
// the currently loaded page is known (no total-count endpoint), so
// `wholeSetLoaded` marks the case where page 0 has no further pages — the only
// time a zero `readyCount` is authoritative for the entire set.
export interface GoldenStats {
  readyCount: number;
  wholeSetLoaded: boolean;
}

export const EMPTY_GOLDEN_STATS: GoldenStats = { readyCount: 0, wholeSetLoaded: false };

interface GoldenSetSectionProps {
  onStats: (stats: GoldenStats) => void;
}

export default function GoldenSetSection({ onStats }: GoldenSetSectionProps) {
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<ProjectedQueryLog[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState('');

  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

  useEffect(() => {
    // Stale-response guard: page/reload changes (or unmount) while a request is
    // in flight — the cleanup flips this so the late response is dropped.
    let cancelled = false;
    setIsLoading(true);
    setError('');
    fetchQueryLogs({ evalSet: true, page })
      .then((data) => {
        if (cancelled) return;
        setRows(data.logs);
        setHasMore(data.hasMore);
        const readyCount = data.logs.filter((row) => row.hasReferenceAnswer).length;
        onStats({ readyCount, wholeSetLoaded: page === 0 && !data.hasMore });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load the golden set.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, reloadToken, onStats]);

  // Removing from the set is a reversible PATCH (re-flaggable from the Queries
  // tab), so no confirm — just disable the row's button while it's in flight.
  const handleRemove = async (id: string) => {
    if (removingId) return;
    setRemovingId(id);
    setRemoveError('');
    try {
      await patchQueryLogAudit(id, { evalSet: false });
      refetch();
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Failed to remove from the golden set.');
    } finally {
      setRemovingId(null);
    }
  };

  const countLabel = `${rows.length}${hasMore ? '+' : ''}`;

  return (
    <section className="eval-section">
      <div className="eval-section-head">
        <div className="eval-section-title">
          Golden Set
          <span className="eval-count">{countLabel}</span>
        </div>
        <div className="eval-head-actions">
          <button
            type="button"
            className="eval-btn-refresh"
            title="Refresh"
            aria-label="Refresh golden set"
            onClick={refetch}
            disabled={isLoading}
          >
            ↻
          </button>
          <button
            type="button"
            className="eval-btn-create"
            onClick={() => setCreateOpen(true)}
          >
            Create
          </button>
        </div>
      </div>

      {error && <div className="eval-error">{error}</div>}
      {removeError && <div className="eval-error">{removeError}</div>}

      {rows.length === 0 && !isLoading && !error ? (
        <div className="eval-empty">
          No golden queries yet. Create one, or flag existing queries via Triage in the Queries
          tab.
        </div>
      ) : (
        <div className="eval-table-card">
          <table className="eval-table">
            <thead>
              <tr>
                <th>Question</th>
                <th>Added</th>
                <th>By</th>
                <th>Readiness</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="eval-table-status">
                    <span className="eval-table-status-inner">
                      <span className="eval-spinner" />
                      Loading golden set…
                    </span>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="eval-question" title={row.question}>
                        {row.question}
                      </div>
                    </td>
                    <td className="eval-cell-time">{formatDateTime(row.askedAt)}</td>
                    <td>{row.askedBy}</td>
                    <td>
                      {row.hasReferenceAnswer ? (
                        <span className="eval-chip eval-chip-ready">ready</span>
                      ) : (
                        <span
                          className="eval-chip eval-chip-needs"
                          title="Runs score entries that have an answer — a custom answer, or the chatbot's own answer."
                        >
                          needs answer
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="eval-actions">
                        <button
                          type="button"
                          className="eval-btn-icon eval-btn-edit"
                          title="Edit custom answer"
                          aria-label="Edit custom answer"
                          onClick={() => setEditId(row.id)}
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          className="eval-btn-icon eval-btn-remove"
                          title="Remove from golden set"
                          aria-label="Remove from golden set"
                          disabled={removingId === row.id}
                          onClick={() => handleRemove(row.id)}
                        >
                          {removingId === row.id ? <span className="eval-spinner" /> : '✕'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {(rows.length > 0 || page > 0) && (
        <div className="eval-pager">
          <span className="eval-page-label">Page {page + 1}</span>
          <button
            type="button"
            className="eval-btn-page"
            onClick={() => setPage((prev) => Math.max(0, prev - 1))}
            disabled={page === 0 || isLoading}
          >
            Prev
          </button>
          <button
            type="button"
            className="eval-btn-page"
            onClick={() => setPage((prev) => prev + 1)}
            disabled={!hasMore || isLoading}
          >
            Next
          </button>
        </div>
      )}

      {/* Edit an existing golden entry's custom answer. */}
      <CustomAnswerModal
        queryId={editId}
        onClose={() => setEditId(null)}
        onChanged={refetch}
      />
      {/* Create straight into the golden set — createInEvalSet threads through
          to createCustomQueryLog so the new Q&A lands with evalSet:true. */}
      <CustomAnswerModal
        queryId={null}
        createOpen={isCreateOpen}
        createInEvalSet
        onClose={() => setCreateOpen(false)}
        onChanged={refetch}
      />
    </section>
  );
}
